import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './DraftOrdersPage.css';

const POLL_MS = 2000;
const ACCEPT  = '.pdf,.xlsx,.xls,.xlsm';

function distStatusLabel(status) {
  switch (status) {
    case 'pending':
    case 'running':                       return 'Extracting…';
    case 'awaiting_confirmation':         return 'Awaiting distributor confirmation';
    case 'awaiting_carton_confirmation':  return 'Awaiting carton conversion review';
    case 'creating':                      return 'Working…';
    case 'complete':                      return 'Complete';
    case 'error':                         return 'Error';
    case 'cancelled':                     return 'Cancelled';
    default:                              return '';
  }
}

const TERMINAL_STATUSES = ['complete', 'error', 'cancelled'];

export default function DraftOrdersPage() {
  const [files,         setFiles]         = useState([]);
  const [jobId,         setJobId]         = useState(null);
  const [status,        setStatus]        = useState(null);   // pending/running/awaiting_confirmation/creating/complete/error
  const [logs,          setLogs]          = useState([]);
  const [warnings,      setWarnings]      = useState([]);
  const [results,       setResults]       = useState([]);
  const [pendingPos,    setPendingPos]    = useState([]);
  const [distCustomers, setDistCustomers] = useState([]);
  const [discountCfg,   setDiscountCfg]   = useState(null);  // {default_discount, accounts}
  const [selections,    setSelections]    = useState({});   // key -> {customer_id, skip}
  const [flagResolutions, setFlagResolutions] = useState({}); // poKey -> { sku: { quantity, quantitySource, unit_price, unit_priceSource } }
  const [products,      setProducts]      = useState([]);    // [{sku, title}] — SKU autocomplete for replace/add
  const [addedItems,    setAddedItems]    = useState({});    // poKey -> [{ tempId, sku, quantity }] — reviewer-added lines
  const [newItemDraft,  setNewItemDraft]  = useState({});    // poKey -> { sku, quantity } — the pending "add line item" form
  const [cartonSkip,    setCartonSkip]    = useState({});    // poKey -> bool — "skip this PO" at the carton-review step
  const [cartonEdits,   setCartonEdits]   = useState({});    // poKey -> { sku: { master_qty, inner_qty, unit_qty } } — reviewer-adjusted carton breakdown
  const [error,         setError]         = useState(null);
  const [dragging,      setDragging]      = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [submittingConfirm, setSubmittingConfirm] = useState(false);
  const [resumableJobs, setResumableJobs]  = useState([]); // unfinished jobs from any previous session
  const [cancelling,    setCancelling]     = useState(false);

  const fileInputRef = useRef();
  const logBodyRef   = useRef();
  const pollRef      = useRef();

  const refreshResumableJobs = useCallback(() => {
    axios.get('/api/dist-orders/jobs')
      .then(r => setResumableJobs(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.get('/api/dist-orders/dist-customers')
      .then(r => setDistCustomers(r.data || []))
      .catch(() => {});
    axios.get('/api/dist-orders/config/discounts')
      .then(r => setDiscountCfg(r.data))
      .catch(() => {});
    axios.get('/api/dist-orders/products')
      .then(r => setProducts(r.data || []))
      .catch(() => {});
    refreshResumableJobs();
  }, [refreshResumableJobs]);

  // Mirrors the backend's _dist_resolve_discount account-level lookup (SKU
  // overrides aren't known per PO here, so this shows the account's base rate
  // as a heads-up before the order is created).
  function resolveDiscountPreview(customerId) {
    if (!discountCfg) return null;
    const customer = distCustomers.find(c => c.id === customerId);
    const tags = new Set((customer?.tags || []).map(t => t.toUpperCase()));
    const acct = (discountCfg.accounts || []).find(a => tags.has((a.tag || '').toUpperCase()));
    if (acct) {
      const skuCount = Object.keys(acct.sku_overrides || {}).length;
      return `${acct.discount}% (${acct.label}${skuCount ? ` — ${skuCount} SKU override${skuCount === 1 ? '' : 's'}` : ''})`;
    }
    return `${discountCfg.default_discount}% (default)`;
  }

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const poll = useCallback((jid) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`/api/dist-orders/status/${jid}`);
        const d = r.data;
        setLogs(d.logs || []);
        setWarnings(d.warnings || []);
        setResults(d.results || []);
        setPendingPos(d.pending_pos || []);
        setStatus(d.status);
        if (d.status === 'awaiting_confirmation') {
          setSelections(prev => {
            const next = { ...prev };
            (d.pending_pos || []).forEach(p => {
              if (!next[p.key]) {
                next[p.key] = { customer_id: p.guessed_customer_id || '', skip: false };
              }
            });
            return next;
          });
        }
        if (TERMINAL_STATUSES.includes(d.status)) {
          if (d.error) setError(d.error);
          stopPolling();
          setLoading(false);
        }
      } catch (e) {
        setError('Lost connection to server');
        stopPolling();
        setLoading(false);
      }
    }, POLL_MS);
  }, []);

  useEffect(() => {
    // Scroll only the log panel's own scroll container, not the page — this
    // used to call scrollIntoView() on a sentinel, which drags the whole page
    // down to the log panel on every poll tick even when the user has
    // scrolled up to read the warnings/confirmation panels above it.
    if (logBodyRef.current) logBodyRef.current.scrollTop = logBodyRef.current.scrollHeight;
  }, [logs]);

  useEffect(() => () => stopPolling(), []);

  function addFiles(incoming) {
    const allowed = Array.from(incoming).filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ['pdf','xlsx','xls','xlsm'].includes(ext);
    });
    setFiles(prev => {
      const names = new Set(prev.map(f => f.name));
      return [...prev, ...allowed.filter(f => !names.has(f.name))];
    });
  }

  function removeFile(name) {
    setFiles(prev => prev.filter(f => f.name !== name));
  }

  function reset() {
    stopPolling();
    setFiles([]);
    setJobId(null);
    setStatus(null);
    setLogs([]);
    setWarnings([]);
    setResults([]);
    setPendingPos([]);
    setSelections({});
    setFlagResolutions({});
    setAddedItems({});
    setNewItemDraft({});
    setCartonSkip({});
    setCartonEdits({});
    setError(null);
    setLoading(false);
    refreshResumableJobs();
  }

  // Picks back up a job left running (or awaiting confirmation) from any
  // previous session — this tab's own reload, a different tab, or a
  // different browser entirely, since the job itself lives server-side.
  function resumeJob(job) {
    setJobId(job.job_id);
    setStatus(job.status);
    setLoading(true);
    setError(null);
    poll(job.job_id);
  }

  // Stops requesting further work for this job — already-issued API/Shopify
  // calls finish, but the loop won't start the next file/PO. The job itself
  // (and anything it already created) is untouched; this just walks away
  // from watching it in this tab.
  async function cancelJob() {
    if (!jobId) return;
    setCancelling(true);
    try {
      await axios.post(`/api/dist-orders/cancel/${jobId}`);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setCancelling(false);
    }
  }

  function setSelection(key, patch) {
    setSelections(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  function setFlagResolution(poKey, sku, patch) {
    setFlagResolutions(prev => ({
      ...prev,
      [poKey]: { ...prev[poKey], [sku]: { ...(prev[poKey] || {})[sku], ...patch } },
    }));
  }

  // Reviewer's edit to one line item's carton breakdown at the step-2 review
  // — e.g. correcting the Master/Inner count by hand when the auto-derived
  // conversion looks wrong for this SKU (see the "⚙ Carton Conversions" page
  // for fixing it permanently instead, for every future order).
  function setCartonEdit(poKey, sku, patch) {
    setCartonEdits(prev => ({
      ...prev,
      [poKey]: { ...prev[poKey], [sku]: { ...(prev[poKey] || {})[sku], ...patch } },
    }));
  }

  // Reviewer-added line items — a SKU (picked from the live catalog, so it's
  // always valid) plus a quantity, not present in the extracted PO at all.
  function addLineItem(poKey) {
    const draft = newItemDraft[poKey] || {};
    const sku   = (draft.sku || '').trim();
    const qty   = parseInt(draft.quantity, 10);
    if (!sku || !products.some(p => p.sku === sku) || !qty || qty <= 0) return;
    const tempId = `added-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setAddedItems(prev => ({ ...prev, [poKey]: [...(prev[poKey] || []), { tempId, sku, quantity: qty }] }));
    setNewItemDraft(prev => ({ ...prev, [poKey]: { sku: '', quantity: '' } }));
  }

  function removeLineItem(poKey, tempId) {
    setAddedItems(prev => ({ ...prev, [poKey]: (prev[poKey] || []).filter(it => it.tempId !== tempId) }));
  }

  function updateAddedItem(poKey, tempId, patch) {
    setAddedItems(prev => ({
      ...prev,
      [poKey]: (prev[poKey] || []).map(it => (it.tempId === tempId ? { ...it, ...patch } : it)),
    }));
  }

  // A flag counts as resolved once the reviewer has picked one of the two
  // independently-read values, or typed their own after checking the source PO.
  function isFlagResolved(poKey, f) {
    const res = (flagResolutions[poKey] || {})[f.sku] || {};
    if (f.issue === 'price_mismatch') {
      return res.unit_priceSource === 'pass1' || res.unit_priceSource === 'pass2' ||
        (res.unit_priceSource === 'manual' && res.unit_price !== undefined && res.unit_price !== '');
    }
    // quantity_mismatch and missing_in_second_pass both resolve on the quantity field
    return res.quantitySource === 'pass1' || res.quantitySource === 'pass2' ||
      (res.quantitySource === 'manual' && res.quantity !== undefined && res.quantity !== '');
  }

  function isPoResolved(p) {
    return (p.verify_flags || []).every(f => isFlagResolved(p.key, f));
  }

  async function handleConfirmDistributors() {
    if (!jobId) return;
    setSubmittingConfirm(true);
    setError(null);
    const confirmations = pendingPos.map(p => {
      const sel      = selections[p.key] || {};
      const resolved = flagResolutions[p.key] || {};
      const resolutions = {};
      Object.entries(resolved).forEach(([sku, r]) => {
        const entry = {};
        if (r.quantity !== undefined && r.quantity !== '') entry.quantity = r.quantity;
        if (r.unit_price !== undefined && r.unit_price !== '') entry.unit_price = r.unit_price;
        if (r.sku !== undefined && r.sku !== '') entry.sku = r.sku;
        if (Object.keys(entry).length) resolutions[sku] = entry;
      });
      const additional_items = (addedItems[p.key] || [])
        .filter(it => it.sku && parseInt(it.quantity, 10) > 0)
        .map(it => ({ sku: it.sku, quantity: parseInt(it.quantity, 10) }));
      return { key: p.key, customer_id: sel.customer_id || '', skip: !!sel.skip || !sel.customer_id, resolutions, additional_items };
    });
    try {
      await axios.post('/api/dist-orders/confirm-units', { job_id: jobId, confirmations });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSubmittingConfirm(false);
    }
  }

  // Step 2 -> creation: the carton breakdown has been reviewed (and possibly
  // hand-adjusted), actually create the Shopify draft orders now. skip_keys
  // lets a PO be pulled out at this final step too, if the breakdown itself
  // looks wrong. carton_overrides carries only the lines the reviewer
  // actually touched — everything else still gets a fresh auto-computed
  // split at creation time, same as always.
  async function handleCreateOrders() {
    if (!jobId) return;
    setSubmittingConfirm(true);
    setError(null);
    const skip_keys = Object.entries(cartonSkip).filter(([, v]) => v).map(([k]) => k);
    const carton_overrides = {};
    pendingPos.forEach(p => {
      const poEdits = cartonEdits[p.key];
      if (!poEdits) return;
      const skuOverrides = {};
      Object.entries(poEdits).forEach(([sku, e]) => {
        skuOverrides[sku] = {
          master_qty: parseInt(e.master_qty, 10) || 0,
          inner_qty:  parseInt(e.inner_qty, 10)  || 0,
          unit_qty:   parseInt(e.unit_qty, 10)   || 0,
        };
      });
      if (Object.keys(skuOverrides).length) carton_overrides[p.key] = skuOverrides;
    });
    try {
      await axios.post('/api/dist-orders/create', { job_id: jobId, skip_keys, carton_overrides });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSubmittingConfirm(false);
    }
  }

  async function handleProcess() {
    if (!files.length) return;
    setLoading(true);
    setError(null);
    setLogs([]);
    setWarnings([]);
    setResults([]);
    setStatus('pending');

    const form = new FormData();
    files.forEach(f => form.append('files', f));

    try {
      const r  = await axios.post('/api/dist-orders/upload', form);
      const jid = r.data.job_id;
      setJobId(jid);
      poll(jid);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setStatus('error');
      setLoading(false);
    }
  }

  // Renders the pass1/pass2/manual radio choice for one flagged field on one SKU.
  function renderFlagChoice(poKey, sku, field, val1, val2, label) {
    const res    = (flagResolutions[poKey] || {})[sku] || {};
    const source = res[`${field}Source`];
    const update = patch => setFlagResolution(poKey, sku, patch);
    return (
      <div className="do-flag-options">
        <label className="do-flag-option">
          <input type="radio" name={`${poKey}-${sku}-${field}`} checked={source === 'pass1'}
            onChange={() => update({ [field]: val1, [`${field}Source`]: 'pass1' })} />
          {val2 !== undefined ? `Use ${val1} (first read)` : `Confirm ${val1} is correct`}
        </label>
        {val2 !== undefined && (
          <label className="do-flag-option">
            <input type="radio" name={`${poKey}-${sku}-${field}`} checked={source === 'pass2'}
              onChange={() => update({ [field]: val2, [`${field}Source`]: 'pass2' })} />
            Use {val2} (second read)
          </label>
        )}
        <label className="do-flag-option">
          <input type="radio" name={`${poKey}-${sku}-${field}`} checked={source === 'manual'}
            onChange={() => update({ [`${field}Source`]: 'manual' })} />
          Other:
          <input
            type="number"
            className="do-flag-manual-input"
            placeholder={label}
            value={source === 'manual' ? (res[field] ?? '') : ''}
            disabled={source !== 'manual'}
            onChange={e => update({ [field]: e.target.value, [`${field}Source`]: 'manual' })}
          />
        </label>
      </div>
    );
  }

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const isRunning  = status === 'pending' || status === 'running' || status === 'creating';
  const isTerminal = TERMINAL_STATUSES.includes(status);

  return (
    <div className="do-page">
      <datalist id="do-sku-catalog">
        {products.map(p => <option key={p.sku} value={p.sku}>{p.title}</option>)}
      </datalist>
      <div className="do-title-row">
        <h1>Distributor Draft Orders</h1>
        <div className="do-title-actions">
          {jobId && (
            <button className="do-btn-secondary" onClick={reset}>← Back</button>
          )}
          {jobId && !isTerminal && (
            <button className="do-btn-secondary do-btn-danger" onClick={cancelJob} disabled={cancelling}>
              {cancelling ? 'Cancelling…' : 'Cancel Run'}
            </button>
          )}
          <Link to="/draft-orders/config" className="do-btn-secondary">⚙ Discounts</Link>
          <Link to="/draft-orders/po-mapping" className="do-btn-secondary">⚙ PO Mapping</Link>
          <Link to="/draft-orders/carton-conversions" className="do-btn-secondary">⚙ Carton Conversions</Link>
          <Link to="/draft-orders/pending-conversion" className="do-btn-secondary">📋 Pending Conversions</Link>
        </div>
      </div>
      <p className="do-sub">
        Upload PO files (PDF or Excel). Claude extracts the line items and guesses the distributor; you confirm the
        distributor and quantities, then review the Master/Inner carton conversion as a separate step, before
        Shopify draft orders are created and a Slack notification is sent.
      </p>
      {jobId && (
        <p className="do-resume-note">
          ← Back leaves this job running in the background — resume it later from the list on this screen.
        </p>
      )}

      {!jobId && resumableJobs.length > 0 && (
        <div className="do-resume">
          <h3 className="do-section-title">Resume Unfinished Session{resumableJobs.length !== 1 ? 's' : ''}</h3>
          {resumableJobs.map(j => (
            <div key={j.job_id} className="do-resume-card">
              <div className="do-resume-info">
                <span className="do-resume-files">{(j.files || []).join(', ') || 'Untitled batch'}</span>
                <span className="do-resume-status">{distStatusLabel(j.status)}</span>
                {j.created && (
                  <span className="do-resume-time">{new Date(j.created * 1000).toLocaleString()}</span>
                )}
              </div>
              <button className="do-btn-secondary" onClick={() => resumeJob(j)}>Resume</button>
            </div>
          ))}
        </div>
      )}

      {!jobId && (
        <>
          {/* Drop zone */}
          <div
            className={`do-dropzone ${dragging ? 'do-dropzone--active' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              multiple
              style={{ display: 'none' }}
              onChange={e => addFiles(e.target.files)}
            />
            <div className="do-dropzone-icon">📂</div>
            <p className="do-dropzone-label">Drop PO files here or <span className="do-link">browse</span></p>
            <p className="do-dropzone-hint">PDF, XLSX, XLS accepted</p>
          </div>

          {/* File list */}
          {files.length > 0 && (
            <ul className="do-file-list">
              {files.map(f => (
                <li key={f.name} className="do-file-item">
                  <span className="do-file-icon">{f.name.endsWith('.pdf') ? '📄' : '📊'}</span>
                  <span className="do-file-name">{f.name}</span>
                  <span className="do-file-size">{(f.size / 1024).toFixed(1)} KB</span>
                  <button className="do-file-remove" onClick={() => removeFile(f.name)}>✕</button>
                </li>
              ))}
            </ul>
          )}

          <div className="do-actions">
            <button
              className="do-btn-primary"
              onClick={handleProcess}
              disabled={!files.length || loading}
            >
              {loading ? 'Processing…' : `Process ${files.length} File${files.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="do-warnings">
          <h3 className="do-section-title">⚠ Order Match Warning</h3>
          {warnings.map((w, i) => (
            <div key={i} className="do-warning-item">{w}</div>
          ))}
        </div>
      )}

      {/* Step 1: distributor + individual-unit confirmation */}
      {status === 'awaiting_confirmation' && pendingPos.length > 0 && (
        <div className="do-confirm">
          <h3 className="do-section-title">Step 1 of 2 — Confirm Distributor &amp; Quantities</h3>
          <p className="do-confirm-hint">
            Pick the correct distributor and fix up line items as plain individual units — Master/Inner carton
            conversion happens as its own review step next, once these numbers are settled.
          </p>
          {pendingPos.map(p => {
            const sel = selections[p.key] || {};
            return (
              <div key={p.key} className={`do-confirm-card ${sel.skip ? 'do-confirm-card--done' : ''}`}>
                <div className="do-confirm-head">
                  <span className="do-confirm-po">PO {p.po}</span>
                  <span className="do-confirm-file">{p.file}</span>
                </div>
                {p.extraction_method && (
                  <div className="do-confirm-method">
                    <span className="do-confirm-method-label">Extracted via: {p.extraction_method}</span>
                  </div>
                )}
                {p.guessed_customer_name && (
                  <p className="do-confirm-guess">Guessed: {p.guessed_customer_name}</p>
                )}
                {p.mei_pdf_note && (
                  <p className="do-confirm-note">
                    📋 Micro Center PDF PO — this format has misread quantities before (rotated table layout).
                    Double-check the extracted quantities against the source PO before confirming.
                  </p>
                )}
                {p.unmatched && (
                  <p className="do-confirm-warn">
                    ⚠ Not found in the distributor list (checked live against Shopify). If this is a
                    new distributor, add them in Shopify tagged "DIST" first, then re-run this file.
                  </p>
                )}
                {p.verify_flags && p.verify_flags.length > 0 && (
                  <div className="do-confirm-warn do-flag-block">
                    ⚠ A second, independent read of the PO disagreed with the first extraction on{' '}
                    {p.verify_flags.length} line item{p.verify_flags.length !== 1 ? 's' : ''} — check the PO and
                    resolve each one below before this PO can be confirmed:
                    {p.verify_flags.map((f, i) => (
                      <div className="do-flag-item" key={i}>
                        <div className="do-flag-sku">{f.sku}</div>
                        {f.issue === 'quantity_mismatch' &&
                          renderFlagChoice(p.key, f.sku, 'quantity', f.pass1_qty, f.pass2_qty, 'quantity')}
                        {f.issue === 'price_mismatch' &&
                          renderFlagChoice(p.key, f.sku, 'unit_price', f.pass1_price, f.pass2_price, 'unit price')}
                        {f.issue === 'missing_in_second_pass' &&
                          renderFlagChoice(p.key, f.sku, 'quantity', f.pass1_qty, undefined, 'quantity')}
                      </div>
                    ))}
                  </div>
                )}
                {p.oos_flags && p.oos_flags.length > 0 && (
                  <div className="do-confirm-warn do-flag-block">
                    ⚠ {p.oos_flags.length} line item{p.oos_flags.length !== 1 ? 's' : ''} requested more than Shipping
                    Pilot has on hand — quantity capped to the max available (shown as "Qty" below, still editable
                    if more is coming in):
                    {p.oos_flags.map((f, i) => (
                      <div className="do-flag-item" key={i}>
                        <div className="do-flag-sku">{f.sku}</div>
                        <div className="do-flag-choice">
                          Requested {f.requested}, {f.available} available — capped to {f.available}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {p.rows && p.rows.length > 0 && (
                  <div className="do-preview">
                    <div className="do-preview-header">Line Items ({p.rows.length}) — review before this goes to Shopify</div>
                    <div className="do-preview-table-wrap">
                      <table className="do-preview-table">
                        <thead>
                          <tr><th>SKU</th><th>Description</th><th>Qty</th><th>Unit Price</th></tr>
                        </thead>
                        <tbody>
                          {p.rows.map((row, i) => {
                            const res    = (flagResolutions[p.key] || {})[row.sku] || {};
                            const effSku = res.sku !== undefined ? res.sku : row.sku;
                            const effQty = (res.quantity !== undefined && res.quantity !== '') ? res.quantity : row.quantity;
                            const edited = (res.sku !== undefined && res.sku !== row.sku) ||
                              (res.quantity !== undefined && res.quantity !== '' && String(res.quantity) !== String(row.quantity));
                            const oosFlag = (p.oos_flags || []).find(f => f.sku === row.sku);
                            return (
                              <tr key={i} className={[edited && 'do-preview-row--edited', oosFlag && 'do-preview-row--oos'].filter(Boolean).join(' ')}>
                                <td>
                                  <input
                                    className="do-preview-input"
                                    list="do-sku-catalog"
                                    defaultValue={effSku}
                                    onBlur={e => {
                                      const v = e.target.value.trim();
                                      if (v && v !== row.sku) setFlagResolution(p.key, row.sku, { sku: v });
                                    }}
                                  />
                                </td>
                                <td className="do-preview-desc">{row.description}</td>
                                <td>
                                  <input
                                    type="number"
                                    className="do-preview-input do-preview-input--qty"
                                    defaultValue={effQty}
                                    onBlur={e => {
                                      const v = e.target.value;
                                      if (v !== '' && v !== String(row.quantity)) {
                                        setFlagResolution(p.key, row.sku, { quantity: v, quantitySource: 'manual' });
                                      }
                                    }}
                                  />
                                  {oosFlag && (
                                    <div className="do-preview-oos-note">
                                      OOS — requested {oosFlag.requested}, only {oosFlag.available} in stock
                                    </div>
                                  )}
                                </td>
                                <td className="do-preview-price">{row.unit_price}</td>
                              </tr>
                            );
                          })}
                          {(addedItems[p.key] || []).map(item => (
                            <tr key={item.tempId} className="do-preview-row--edited">
                              <td>
                                <input
                                  className="do-preview-input"
                                  list="do-sku-catalog"
                                  defaultValue={item.sku}
                                  onBlur={e => updateAddedItem(p.key, item.tempId, { sku: e.target.value.trim() })}
                                />
                              </td>
                              <td className="do-preview-desc">
                                Added by reviewer
                                <button type="button" className="do-btn-remove-line" onClick={() => removeLineItem(p.key, item.tempId)}>✕</button>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="do-preview-input do-preview-input--qty"
                                  defaultValue={item.quantity}
                                  onBlur={e => updateAddedItem(p.key, item.tempId, { quantity: e.target.value })}
                                />
                              </td>
                              <td className="do-preview-price">—</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="do-preview-add-item">
                      <input
                        className="do-preview-input"
                        list="do-sku-catalog"
                        placeholder="Add SKU…"
                        value={(newItemDraft[p.key] || {}).sku || ''}
                        onChange={e => setNewItemDraft(prev => ({ ...prev, [p.key]: { ...prev[p.key], sku: e.target.value } }))}
                      />
                      <input
                        type="number"
                        className="do-preview-input do-preview-input--qty"
                        placeholder="Qty"
                        value={(newItemDraft[p.key] || {}).quantity || ''}
                        onChange={e => setNewItemDraft(prev => ({ ...prev, [p.key]: { ...prev[p.key], quantity: e.target.value } }))}
                      />
                      <button type="button" className="do-btn-secondary" onClick={() => addLineItem(p.key)}>
                        + Add line item
                      </button>
                    </div>
                  </div>
                )}
                <div className="do-confirm-row">
                  <select
                    className="do-confirm-select"
                    value={sel.skip ? '' : (sel.customer_id || '')}
                    onChange={e => setSelection(p.key, { customer_id: e.target.value, skip: false })}
                  >
                    <option value="">Select distributor…</option>
                    {distCustomers.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.display}{c.location ? ` — ${c.location}` : ''}{c.is_gts ? ' (GTS)' : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    className="do-btn-skip"
                    onClick={() => setSelection(p.key, { skip: !sel.skip })}
                  >
                    {sel.skip ? 'Undo skip' : 'Skip this PO'}
                  </button>
                  {sel.skip && <span className="do-confirm-status">Will be skipped</span>}
                </div>
                {!sel.skip && sel.customer_id && (
                  <p className="do-confirm-discount">
                    Discount to be applied: <strong>{resolveDiscountPreview(sel.customer_id)}</strong>
                  </p>
                )}
              </div>
            );
          })}
          {(() => {
            const unresolvedCount = pendingPos.filter(p => {
              const sel = selections[p.key] || {};
              return !sel.skip && !isPoResolved(p);
            }).length;
            return (
              <div className="do-confirm-actions">
                <button
                  className="do-btn-primary"
                  onClick={handleConfirmDistributors}
                  disabled={submittingConfirm || unresolvedCount > 0}
                >
                  {submittingConfirm ? 'Submitting…' : 'Continue to Carton Review →'}
                </button>
                {unresolvedCount > 0 && (
                  <span className="do-confirm-status">
                    Resolve the flagged line item{unresolvedCount !== 1 ? 's' : ''} on {unresolvedCount} PO{unresolvedCount !== 1 ? 's' : ''} above first
                  </span>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Step 2: Master/Inner carton conversion review */}
      {status === 'awaiting_carton_confirmation' && (
        <div className="do-confirm">
          <h3 className="do-section-title">Step 2 of 2 — Review Carton Conversion</h3>
          <p className="do-confirm-hint">
            Each finalized line item, exploded into Master ("MA-") / Inner ("IN-") cartons plus any individual-unit
            remainder. Nothing has been sent to Shopify yet — the Master/Inner/Unit counts below are editable if the
            auto-computed split needs a manual correction for this order (fix it permanently for every future order
            instead on the ⚙ Carton Conversions page).
          </p>
          {pendingPos.filter(p => p.finalized_rows && p.finalized_rows.length > 0 && !p.confirm_skip).map(p => {
            const skipped = !!cartonSkip[p.key];
            return (
              <div key={p.key} className={`do-confirm-card ${skipped ? 'do-confirm-card--done' : ''}`}>
                <div className="do-confirm-head">
                  <span className="do-confirm-po">PO {p.po}</span>
                  <span className="do-confirm-file">{p.file}</span>
                </div>
                <p className="do-confirm-guess">{p.customer_name || p.guessed_customer_name}</p>
                <div className="do-preview">
                  <div className="do-preview-header">Carton Breakdown ({p.carton_lines.length} line item{p.carton_lines.length !== 1 ? 's' : ''})</div>
                  <div className="do-preview-table-wrap">
                    <table className="do-preview-table">
                      <thead>
                        <tr>
                          <th>SKU</th><th>Description</th><th>Qty</th>
                          <th>Master</th><th>Inner</th><th>Units</th>
                        </tr>
                      </thead>
                      <tbody>
                        {p.carton_lines.map((line, i) => {
                          const edit = (cartonEdits[p.key] || {})[line.sku] || {};
                          const masterQty = edit.master_qty !== undefined ? edit.master_qty : line.master_qty;
                          const innerQty  = edit.inner_qty  !== undefined ? edit.inner_qty  : line.inner_qty;
                          const unitQty   = edit.unit_qty   !== undefined ? edit.unit_qty   : line.unit_qty;
                          const edited = edit.master_qty !== undefined || edit.inner_qty !== undefined || edit.unit_qty !== undefined;
                          return (
                            <tr key={i} className={edited ? 'do-preview-row--edited' : ''}>
                              <td>{line.sku}</td>
                              <td className="do-preview-desc">{line.description}</td>
                              <td>{line.quantity}</td>
                              <td>
                                <input
                                  type="number"
                                  className="do-preview-input do-preview-input--qty"
                                  defaultValue={masterQty}
                                  onBlur={e => setCartonEdit(p.key, line.sku, { master_qty: e.target.value })}
                                />
                                {line.master_size ? <div className="do-preview-carton-note">×{line.master_size}/ea</div> : null}
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="do-preview-input do-preview-input--qty"
                                  defaultValue={innerQty}
                                  onBlur={e => setCartonEdit(p.key, line.sku, { inner_qty: e.target.value })}
                                />
                                {line.inner_size ? <div className="do-preview-carton-note">×{line.inner_size}/ea</div> : null}
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="do-preview-input do-preview-input--qty"
                                  defaultValue={unitQty}
                                  onBlur={e => setCartonEdit(p.key, line.sku, { unit_qty: e.target.value })}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="do-confirm-row">
                  <button
                    className="do-btn-skip"
                    onClick={() => setCartonSkip(prev => ({ ...prev, [p.key]: !prev[p.key] }))}
                  >
                    {skipped ? 'Undo skip' : 'Skip this PO'}
                  </button>
                  {skipped && <span className="do-confirm-status">Will be skipped</span>}
                </div>
              </div>
            );
          })}
          <div className="do-confirm-actions">
            <button
              className="do-btn-primary"
              onClick={handleCreateOrders}
              disabled={submittingConfirm}
            >
              {submittingConfirm ? 'Submitting…' : 'Create Draft Orders'}
            </button>
          </div>
        </div>
      )}

      {/* Log viewer */}
      {(logs.length > 0 || isRunning) && (
        <div className="do-log-section">
          <div className="do-log-header">
            <span className={`do-status-dot ${status}`} />
            <span className="do-log-title">{distStatusLabel(status)}</span>
          </div>
          <div className="do-log-body" ref={logBodyRef}>
            {logs.map((l, i) => <div key={i} className="do-log-line">{l}</div>)}
          </div>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="do-results">
          <h3 className="do-section-title">Draft Orders Created</h3>
          {results.map((r, i) => (
            <div key={i} className="do-result-card">
              <div className="do-result-header">
                <a href={r.url} target="_blank" rel="noreferrer" className="do-result-name">
                  {r.name}
                </a>
                <span className="do-result-po">PO: {r.po}</span>
              </div>
              <div className="do-result-meta">
                <span>File: {r.file}</span>
                {r.customer && <span>Customer: {r.customer}{r.customer_id ? ` (ID ${r.customer_id})` : ' (unmatched)'}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="do-error">{error}</div>
      )}

      {/* Done — reset */}
      {isTerminal && (
        <div className="do-actions" style={{ marginTop: 16 }}>
          <button className="do-btn-secondary" onClick={reset}>Process More Files</button>
        </div>
      )}
    </div>
  );
}
